import { addDays, differenceInHours, endOfHour, isBefore, parseISO, startOfHour } from "date-fns";
import type {
  ExhibitionMercariShopsSettingFragment,
  ExhibitionRakutenSettingFragment,
  ExhibitionYahooShoppingSettingFragment,
} from "../graphql/generated/apollo";
import { isRealNumber } from "./number";

const MAX_IMAGE_COUNT_BASE = 20;
const MAX_IMAGE_COUNT_STORES = 15;
const MAX_IMAGE_COUNT_MERCARI_SHOPS = 20;
const MAX_IMAGE_COUNT_YAHOO_SHOPPING = 20;
const MAX_IMAGE_COUNT_RAKUTEN = 20;

const MAX_DESCRIPTION_BYTES_RAKUTEN = 10240;
const MAX_ITEM_NAME_BYTES_RAKUTEN = 255;
const MAX_VARIANT_NAME_BYTES_RAKUTEN = 32;
const MAX_TAGLINE_BYTES_RAKUTEN = 174;

export type ValidationWarning = {
  field: string;
  reason: string;
  affectedPlatform?: string[];
  affect?: string;
};

export function validate(
  itemName: string,
  itemDescription: string,
  variants: { name: string; stockQuantity: string | number }[],
  itemImages: {
    mime_type?: string;
  }[],
  itemPrice?: number,
  itemCompareAtPrice?: number,
  selectedMarketplaces?: string[],
  mercariShopsSetting?: ExhibitionMercariShopsSettingFragment,
  yahooShoppingSetting?: ExhibitionYahooShoppingSettingFragment,
  rakutenSetting?: ExhibitionRakutenSettingFragment,
) {
  const warnings: ValidationWarning[] = [];
  if (itemName === "") {
    warnings.push({
      field: "商品名",
      reason: "未入力",
      affectedPlatform: selectedMarketplaces,
    });
  }
  if (countStringForYahooShopping(itemName) > 150) {
    warnings.push({
      field: "商品名",
      reason: "150文字（全角75文字）を超えている",
      affectedPlatform: selectedMarketplaces?.filter((v) => ["YAHOO_SHOPPING"].indexOf(v) > -1),
      affect: "出品時に商品名が…で省略され、説明の先頭に元の商品名が追加されます",
    });
  }
  if (itemName.length > 100) {
    warnings.push({
      field: "商品名",
      reason: "100文字を超えている",
      affectedPlatform: selectedMarketplaces?.filter((v) => ["STORES"].indexOf(v) > -1),
      affect: "出品時に商品名が…で省略され、説明の先頭に元の商品名が追加されます",
    });
  }
  if (itemName.length > 130) {
    warnings.push({
      field: "商品名",
      reason: "130文字を超えている",
      affectedPlatform: selectedMarketplaces?.filter((v) => ["MERCARI_SHOPS"].indexOf(v) > -1),
      affect: "出品時に商品名が…で省略され、説明の先頭に元の商品名が追加されます",
    });
  }
  if (itemName.length > 255) {
    warnings.push({
      field: "商品名",
      reason: "255文字を超えている",
      affectedPlatform: selectedMarketplaces?.filter((v) => ["BASE"].indexOf(v) > -1),
      affect: "出品時に商品名が…で省略され、説明の先頭に元の商品名が追加されます",
    });
  }
  if (itemDescription === "") {
    warnings.push({
      field: "説明",
      reason: "未入力",
      affectedPlatform: selectedMarketplaces?.filter((v) => ["STORES", "MERCARI_SHOPS"].indexOf(v) > -1),
    });
  }
  if (itemDescription.length > 5000) {
    warnings.push({
      field: "説明",
      reason: "5000文字を超えている",
      affectedPlatform: selectedMarketplaces?.filter((v) => ["YAHOO_SHOPPING"].indexOf(v) > -1),
    });
  }
  if (!itemPrice) {
    warnings.push({
      field: "価格",
      reason: "未入力または0円",
      affectedPlatform: selectedMarketplaces,
    });
  }
  if (!isRealNumber(itemPrice)) {
    warnings.push({
      field: "価格",
      reason: "数値ではない",
      affectedPlatform: selectedMarketplaces,
    });
  }
  if (itemPrice && itemCompareAtPrice && itemPrice > itemCompareAtPrice) {
    warnings.push({
      field: "元価格",
      reason: "セール価格よりも高い",
      affectedPlatform: selectedMarketplaces?.filter((v) => ["STORES", "YAHOO_SHOPPING"].indexOf(v) > -1),
    });
  }
  if (variants.length > 1 && variants.find((v) => v.name === "")) {
    warnings.push({
      field: "種類名",
      reason: "未入力",
      affectedPlatform: selectedMarketplaces,
    });
  }
  if (variants.length > 10) {
    warnings.push({
      field: "種類",
      reason: "10を超えて登録されている",
      affectedPlatform: selectedMarketplaces?.filter((v) => ["MERCARI_SHOPS"].indexOf(v) > -1),
    });
  }
  if (variants.find((v) => !isRealNumber(v.stockQuantity) || v.stockQuantity < 0)) {
    warnings.push({
      field: "在庫",
      reason: "未入力または0未満",
      affectedPlatform: selectedMarketplaces,
    });
  }
  if (itemImages.length === 0) {
    warnings.push({
      field: "画像",
      reason: "1枚以上登録されていない",
      affectedPlatform: selectedMarketplaces?.filter((v) => ["MERCARI_SHOPS", "STORES"].indexOf(v) > -1),
    });
  }
  if (itemImages.length > MAX_IMAGE_COUNT_BASE) {
    warnings.push({
      field: "画像",
      reason: `${MAX_IMAGE_COUNT_BASE}枚を超えて登録されている`,
      affectedPlatform: selectedMarketplaces?.filter((v) => ["BASE"].indexOf(v) > -1),
      affect: `出品時に最初の${MAX_IMAGE_COUNT_BASE}枚までしか表示されません`,
    });
  }
  if (itemImages.length > MAX_IMAGE_COUNT_STORES) {
    warnings.push({
      field: "画像",
      reason: `${MAX_IMAGE_COUNT_STORES}枚を超えて登録されている`,
      affectedPlatform: selectedMarketplaces?.filter((v) => ["STORES"].indexOf(v) > -1),
      affect: `出品時に最初の${MAX_IMAGE_COUNT_STORES}枚までしか表示されません。STORES ベーシックプラン（有料）をご利用中の場合はラクダスまでお問い合わせください`,
    });
  }
  if (itemImages.length > MAX_IMAGE_COUNT_MERCARI_SHOPS) {
    warnings.push({
      field: "画像",
      reason: `${MAX_IMAGE_COUNT_MERCARI_SHOPS}枚を超えて登録されている`,
      affectedPlatform: selectedMarketplaces?.filter((v) => ["MERCARI_SHOPS"].indexOf(v) > -1),
      affect: `出品時に最初の${MAX_IMAGE_COUNT_MERCARI_SHOPS}枚までしか表示されません`,
    });
  }
  if (itemImages.length > MAX_IMAGE_COUNT_YAHOO_SHOPPING) {
    warnings.push({
      field: "画像",
      reason: `${MAX_IMAGE_COUNT_YAHOO_SHOPPING}枚を超えて登録されている`,
      affectedPlatform: selectedMarketplaces?.filter((v) => ["YAHOO_SHOPPING"].indexOf(v) > -1),
      affect: `出品時に最初の${MAX_IMAGE_COUNT_YAHOO_SHOPPING}枚までしか表示されません`,
    });
  }
  if (itemImages.length > MAX_IMAGE_COUNT_RAKUTEN) {
    warnings.push({
      field: "画像",
      reason: `${MAX_IMAGE_COUNT_RAKUTEN}枚を超えて登録されている`,
      affectedPlatform: selectedMarketplaces?.filter((v) => ["RAKUTEN"].indexOf(v) > -1),
      affect: `出品時に最初の${MAX_IMAGE_COUNT_RAKUTEN}枚までしか表示されません`,
    });
  }
  if (selectedMarketplaces?.find((v) => v === "MERCARI_SHOPS")) {
    const prefix = "メルカリShopsの";
    if (!mercariShopsSetting) {
      warnings.push({
        field: `${prefix}設定`,
        reason: "未選択",
        affectedPlatform: ["MERCARI_SHOPS"],
      });
    }
    if (!mercariShopsSetting?.category_id) {
      warnings.push({
        field: `${prefix}カテゴリー`,
        reason: "未選択",
        affectedPlatform: ["MERCARI_SHOPS"],
      });
    }
    if (!mercariShopsSetting?.condition) {
      warnings.push({
        field: `${prefix}状態`,
        reason: "未選択",
        affectedPlatform: ["MERCARI_SHOPS"],
      });
    }
    if (!mercariShopsSetting?.shipping_method) {
      warnings.push({
        field: `${prefix}配送方法`,
        reason: "未選択",
        affectedPlatform: ["MERCARI_SHOPS"],
      });
    }
    if (!mercariShopsSetting?.shipping_from_state_id) {
      warnings.push({
        field: `${prefix}発送元の地域`,
        reason: "未選択",
        affectedPlatform: ["MERCARI_SHOPS"],
      });
    }
    if (!mercariShopsSetting?.shipping_duration) {
      warnings.push({
        field: `${prefix}発送までの日数`,
        reason: "未選択",
        affectedPlatform: ["MERCARI_SHOPS"],
      });
    }
  }
  if (selectedMarketplaces?.find((v) => v === "YAHOO_SHOPPING")) {
    const prefix = "Yahoo!ショッピングの";
    if (!yahooShoppingSetting) {
      warnings.push({
        field: `${prefix}設定`,
        reason: "未選択",
        affectedPlatform: ["YAHOO_SHOPPING"],
      });
    }
    if (!yahooShoppingSetting?.product_category) {
      warnings.push({
        field: `${prefix}ショッピングカテゴリ`,
        reason: "未選択",
        affectedPlatform: ["YAHOO_SHOPPING"],
      });
    }
    if (
      !yahooShoppingSetting?.store_category_hierarchies ||
      yahooShoppingSetting.store_category_hierarchies.length === 0 ||
      yahooShoppingSetting.store_category_hierarchies[0].length === 0
    ) {
      warnings.push({
        field: `${prefix}ストアカテゴリ`,
        reason: "未選択",
        affectedPlatform: ["YAHOO_SHOPPING"],
      });
    }
    if (yahooShoppingSetting?.condition && yahooShoppingSetting.condition === 2) {
      if (yahooShoppingSetting?.auction_category === undefined) {
        warnings.push({
          field: `${prefix}オークションカテゴリ`,
          reason: "未選択",
          affectedPlatform: ["YAHOO_SHOPPING"],
        });
      }
      if (yahooShoppingSetting?.auction_shipping_from_prefecture === undefined) {
        warnings.push({
          field: `${prefix}発送元の都道府県`,
          reason: "未選択",
          affectedPlatform: ["YAHOO_SHOPPING"],
        });
      }
    }
  }
  if (selectedMarketplaces?.find((v) => v === "RAKUTEN")) {
    if (countBytesForRakuten(itemName) > MAX_ITEM_NAME_BYTES_RAKUTEN) {
      warnings.push({
        field: "商品名",
        reason: `${MAX_ITEM_NAME_BYTES_RAKUTEN}バイト（約85文字）を超えている`,
        affectedPlatform: ["RAKUTEN"],
        affect: "出品時に商品名が切り詰められる可能性があります",
      });
    }

    if (containsDeviceDependentChars(itemName)) {
      warnings.push({
        field: "商品名",
        reason: "機種依存文字が含まれている",
        affectedPlatform: ["RAKUTEN"],
        affect: "出品時にエラーが発生する可能性があります",
      });
    }

    if (countBytesForRakuten(itemDescription) > MAX_DESCRIPTION_BYTES_RAKUTEN) {
      warnings.push({
        field: "説明",
        reason: `${MAX_DESCRIPTION_BYTES_RAKUTEN}バイト（約3,400文字）を超えている`,
        affectedPlatform: ["RAKUTEN"],
        affect: "出品時に説明文が切り詰められる可能性があります",
      });
    }

    if (containsDeviceDependentChars(itemDescription)) {
      warnings.push({
        field: "説明",
        reason: "機種依存文字が含まれている",
        affectedPlatform: ["RAKUTEN"],
        affect: "出品時にエラーが発生する可能性があります",
      });
    }

    const variantWithLongName = variants.find((v) => countBytesForRakuten(v.name) > MAX_VARIANT_NAME_BYTES_RAKUTEN);
    if (variantWithLongName) {
      warnings.push({
        field: "種類名",
        reason: `${MAX_VARIANT_NAME_BYTES_RAKUTEN}バイト（約10文字）を超えている`,
        affectedPlatform: ["RAKUTEN"],
        affect:
          "出品時に種類名が切り詰められエラーが発生する可能性があります。\n種類名は10文字以内で重複しないようにしてください",
      });
    }

    const variantWithDeviceDependentChars = variants.find((v) => containsDeviceDependentChars(v.name));
    if (variantWithDeviceDependentChars) {
      warnings.push({
        field: "種類名",
        reason: "機種依存文字が含まれている",
        affectedPlatform: ["RAKUTEN"],
        affect: "出品時にエラーが発生する可能性があります",
      });
    }

    if (rakutenSetting?.tagline && countBytesForRakuten(rakutenSetting.tagline) > MAX_TAGLINE_BYTES_RAKUTEN) {
      warnings.push({
        field: "キャッチコピー",
        reason: `${MAX_TAGLINE_BYTES_RAKUTEN}バイト（約58文字）を超えている`,
        affectedPlatform: ["RAKUTEN"],
        affect: "出品時にキャッチコピーが切り詰められる可能性があります",
      });
    }

    if (rakutenSetting?.tagline && containsDeviceDependentChars(rakutenSetting.tagline)) {
      warnings.push({
        field: "キャッチコピー",
        reason: "機種依存文字が含まれている",
        affectedPlatform: ["RAKUTEN"],
        affect: "出品時にエラーが発生する可能性があります",
      });
    }

    // ポイント変倍機能のセットチェック
    const hasPointMultiplierRate =
      rakutenSetting?.point_multiplier_rate !== null && rakutenSetting?.point_multiplier_rate !== undefined;
    const hasPointMultiplierStartsAt =
      rakutenSetting?.point_multiplier_starts_at !== null && rakutenSetting?.point_multiplier_starts_at !== undefined;
    const hasPointMultiplierEndsAt =
      rakutenSetting?.point_multiplier_ends_at !== null && rakutenSetting?.point_multiplier_ends_at !== undefined;

    const pointMultiplierFields = [hasPointMultiplierRate, hasPointMultiplierStartsAt, hasPointMultiplierEndsAt];
    const filledCount = pointMultiplierFields.filter(Boolean).length;

    if (filledCount > 0 && filledCount < 3) {
      warnings.push({
        field: "ポイント変倍機能の変倍率、開始時刻、終了時刻のいずれか",
        reason: "未入力",
        affectedPlatform: ["RAKUTEN"],
        affect: "ポイント変倍が設定されません。全ての項目を入力してください。",
      });
    }

    // ポイント変倍機能の開始時間チェック
    if (rakutenSetting?.point_multiplier_starts_at) {
      const timeValidation = validateRakutenPointMultiplierTime(
        rakutenSetting.point_multiplier_starts_at,
        rakutenSetting.point_multiplier_ends_at,
      );

      if (!timeValidation.isValidStartTime) {
        warnings.push({
          field: "ポイント変倍適用期間　開始日時を00分00秒に切り捨てした時刻",
          reason: "現在時刻から2時間後でない",
          affectedPlatform: ["RAKUTEN"],
        });
      }

      if (!timeValidation.isStartTimeOnHourBoundary) {
        warnings.push({
          field: "ポイント変倍適用期間　開始時間",
          reason: "00分00秒でない",
          affectedPlatform: ["RAKUTEN"],
          affect: "開始時間は自動的に00分00秒に調整されます",
        });
      }
    }

    // ポイント変倍機能の終了時間チェック
    if (rakutenSetting?.point_multiplier_starts_at && rakutenSetting?.point_multiplier_ends_at) {
      const timeValidation = validateRakutenPointMultiplierTime(
        rakutenSetting.point_multiplier_starts_at,
        rakutenSetting.point_multiplier_ends_at,
      );

      if (!timeValidation.isValidEndTime) {
        warnings.push({
          field: "ポイント変倍適用期間　終了日時",
          reason: "開始から60日を超えている",
          affectedPlatform: ["RAKUTEN"],
        });
      }

      if (!timeValidation.isEndTimeOnHourBoundary) {
        warnings.push({
          field: "ポイント変倍適用期間　終了時間",
          reason: "59分59秒でない",
          affectedPlatform: ["RAKUTEN"],
          affect: "終了時間は自動的に59分59秒に調整されます",
        });
      }

      if (!timeValidation.isValidDuration) {
        warnings.push({
          field: "ポイント変倍適用期間",
          reason: "開始時刻と終了時刻の間隔が24時間未満", //23:59:59は24時間とみなす
          affectedPlatform: ["RAKUTEN"],
          affect: "出品時にエラーが発生します。24時間以上の期間を設定してください",
        });
      }
    }

    const prefix = "楽天市場の";
    if (!rakutenSetting) {
      warnings.push({
        field: `${prefix}設定`,
        reason: "未選択",
        affectedPlatform: ["RAKUTEN"],
      });
    }

    if (!rakutenSetting?.genre_id) {
      warnings.push({
        field: `${prefix}ジャンル`,
        reason: "未選択",
        affectedPlatform: ["RAKUTEN"],
      });
    }
  }
  return warnings;
}

// 全角を2文字、半角を1文字として文字数をカウント
function countStringForYahooShopping(str: string) {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: より正確でパフォーマンスの良さそうな処理が見つからなかったので
  return str.length + (str.match(/[^\x01-\x7E]/g)?.length ?? 0);
}

function countBytesForRakuten(str: string): number {
  return new TextEncoder().encode(str).length;
}

function containsDeviceDependentChars(str: string): boolean {
  const pattern = /[①-⑳⑴-⑽⒈-⒛㊤-㊦㊧-㊨㈱㈲㈹㍻㍼㍽㍾〄≒≠∵∑∮∵∥∟⊿∈∋∩∪√‰№℡™㍉㌔㌢㌘㌧㌃㌶㍍㍑㍗㌍㎜㎝㎞㎎㎏㏄㎡㍻]/;
  return pattern.test(str);
}

// 楽天市場のポイント変倍機能の時間バリデーション関数
export function validateRakutenPointMultiplierTime(
  startAt: string,
  endAt?: string,
): {
  isValidStartTime: boolean;
  isValidEndTime: boolean;
  isValidDuration: boolean;
  isStartTimeInFuture: boolean;
  isStartTimeOnHourBoundary: boolean;
  isEndTimeOnHourBoundary: boolean;
  durationInHours: number;
} {
  const startDate = parseISO(startAt);
  const truncatedStartsAt = startOfHour(startDate);
  const now = new Date();

  const diffHours = differenceInHours(truncatedStartsAt, now);
  const isValidStartTime = diffHours >= 2;
  const isStartTimeOnHourBoundary = startDate.getMinutes() === 0 && startDate.getSeconds() === 0;

  let isValidEndTime = true;
  let isValidDuration = true;
  let isEndTimeOnHourBoundary = true;
  let durationInHours = 0;

  if (endAt) {
    const endDate = parseISO(endAt);
    const truncatedStartAt = startOfHour(startDate);
    const roundedEndAt = endOfHour(endDate);
    const maxEndAt = addDays(truncatedStartAt, 60);

    isValidEndTime = isBefore(roundedEndAt, maxEndAt) || roundedEndAt.getTime() === maxEndAt.getTime();
    isEndTimeOnHourBoundary = endDate.getMinutes() === 59 && endDate.getSeconds() === 59;
    durationInHours = Math.ceil(differenceInHours(roundedEndAt, truncatedStartAt));
    isValidDuration = durationInHours >= 23;
  }

  return {
    isValidStartTime,
    isValidEndTime,
    isValidDuration,
    isStartTimeInFuture: isValidStartTime,
    isStartTimeOnHourBoundary,
    isEndTimeOnHourBoundary,
    durationInHours,
  };
}
